<template>
    <div>
        <x-data-table
            class="no-row-hover"
            :headers="headers"
            :items="repayments"
            hide-actions
        >
            <template v-slot:no-data>
                <p class="error-message text-xs-center">No repayments made</p>
            </template>

            <template v-slot:items="props">
                <tr v-if="!(props.item.status === 'draft' && isInsurer)">
                    <td>
                        {{ props.item.effective_date | formatDate }}
                    </td>

                    <td>
                        {{ props.item.reference }}
                    </td>

                    <td class="text-xs-right">
                        {{ props.item.repayment_amount | currency(declaration.currency.code) }}
                    </td>

                    <td class="text-xs-right">
                        {{ getPreviousExposure(props.item.relative_contract_value, props.item.repayment_amount) | currency(declaration.currency.code) }}
                    </td>

                    <td class="text-xs-right">
                        {{ props.item.relative_contract_value | currency(declaration.currency.code) }}
                    </td>

                    <td class="text-xs-right">
                        {{ getLimitOfLiabilityAtTheTime(props.item.relative_contract_value) | currency(declaration.currency.code) }}
                    </td>

                    <td>
                        <status-chip
                            class="ma-0"
                            small
                            :status="props.item.status"
                        />
                    </td>

                    <td class="text-xs-right">
                        <template v-if="showViewDocumentsModal(props.item)">
                            <x-dialog v-model="dialog[props.item.id]" width="900" max-width="900">
                                <template v-slot:activator="{ on }">
                                    <x-btn color="tertiary" small v-on="on">View documents</x-btn>
                                </template>

                                <x-container>
                                    <document-list
                                        v-if="props.item.id"
                                        :documentable-id="props.item.id"
                                        documentable-type="endorsement"
                                        :can-delete="() => false"
                                        :upload=false
                                    />
                                </x-container>
                            </x-dialog>
                        </template>

                        <x-btn
                            v-if="cancelAction && isPendingOrDraftRepayment(props.item)"
                            class="square-icon"
                            color="tertiary"
                            small
                            :disabled="disableCancelRepayment"
                            :loading="cancelRepaymentLoading"
                            @click="cancelRepayment(props.item.id)"
                        >
                            <i class="material-icons">delete</i>
                        </x-btn>

                        <x-btn
                            v-if="props.item.status === 'draft'"
                            class="square-icon ml-2"
                            color="tertiary"
                            small
                            @click="openRepayment(props.item)"
                        >
                            <i class="material-icons">preview</i>
                        </x-btn>
                    </td>
                </tr>
            </template>
        </x-data-table>

        <modal
            ref="cancelRepaymentConfirmation"
            title="Cancel repayment"
            confirmation
            action-text="Yes"
            cancel-text="No"
        >
            <p>
                Are you sure you want to cancel this repayment?
            </p>
        </modal>

        <repayments-dialog :declaration="declaration"/>
    </div>
</template>

<script>
    import StatusChip from '~/components/Shared/UIComponents/StatusChip';
    import Modal from '~/components/Shared/UIComponents/Modal';
    import RepaymentsDialog from '~/components/Shared/UIComponents/RepaymentsDialog';
    import DocumentList from '~/components/Shared/UIComponents/DocumentList';

    export default {
        name: 'RepaymentsTable',
        components: {
            StatusChip,
            Modal,
            RepaymentsDialog,
            DocumentList
        },
        props: {
            repayments: {
                type: Array,
                default: []
            },
            declaration: {
                type: Object,
                default: {}
            },
            cancelAction: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                headers: [
                    {
                        text: 'Repayment Date',
                        value: 'effective_date',
                        width: '10%',
                    },
                    {
                        text: 'Reference',
                        value: 'reference',
                        width: '5%',
                    },
                    {
                        text: 'Repayment Amount',
                        value: 'repayment_amount',
                        align: 'right',
                        width: '10%',
                    },
                    {
                        text: 'Previous Exposure',
                        value: '',
                        align: 'right',
                        width: '15%',
                    },
                    {
                        text: 'New Total Exposure',
                        value: 'relative_contract_value',
                        align: 'right',
                        width: '15%',
                    },
                    {
                        text: 'New Insured Exposure',
                        value: '',
                        align: 'right',
                        width: '15%',
                    },
                    {
                        text: 'Status',
                        sortable: false,
                        width: '10%',
                    },
                    {
                        text: "actions",
                        sortable: false,
                        align: "right",
                        width: "20%",
                    },
                ],
                cancelRepaymentLoading: false,
                disableCancelRepayment: false,
                showRepaymentDialog: false,
                activeRepayment: {},
                dialog: {},
            }
        },
        methods: {
            getLimitOfLiabilityAtTheTime(contractValueAtTheTime) {
                return (this.declaration.indemnity / 100) * contractValueAtTheTime;
            },
            getEstimatedPremium(fieldChanges) {
                if (fieldChanges && fieldChanges.estimated_premium) {
                    return fieldChanges.estimated_premium;
                }
            },
            getChangesInEstimatedPremium(endorsement) {
                if (this.$root.user.company.company_type === 'insurer') {
                    return -Math.abs(endorsement.change_in_insurer_estimated_premium);
                }
                if (endorsement.field_changes && endorsement.field_changes.changes_in_estimated_premium) {
                    return -Math.abs(endorsement.field_changes.changes_in_estimated_premium);
                }
            },
            getPreviousExposure(relativeContractValue, repaymentAmount) {
                return Number(relativeContractValue) + Number(repaymentAmount);
            },
            isPendingOrDraftRepayment(repayment) {
                return ['draft', 'approved'].includes(repayment.status);
            },
            async cancelRepayment(repaymentId) {
                if (await this.$refs.cancelRepaymentConfirmation.open() === false) {
                    return;
                }

                this.loading = true;
                this.cancelRepaymentLoading = true;
                axios.put(this.$root.api(`broker/declaration/${this.declaration.id}/repayment/${repaymentId}/cancel`)
                ).catch(error => {
                    this.loading = false;
                }).then(() => {
                    this.loading = false;
                    this.cancelRepaymentLoading = false;
                    this.disableCancelRepayment = true;

                    this.$eventBus.$emit('update-data');
                });
            },
            openRepayment(repayment) {
                this.$eventBus.$emit('open-dialog', repayment);
            },
            showViewDocumentsModal(repayment) {
                return ['approved', 'applied'].includes(repayment.status);
            },
        },
        computed: {
            isInsurer() {
                return this.$root.user.company && this.$root.user.company.company_type === 'insurer';
            },
        }
    }
</script>
